/**
 * Die Impressum-Angaben als Anzeige — EINE reine Regel: welche Angabe erscheint, mit welcher
 * Quelle und welchem Stand, und welcher eine Satz gilt, wenn es (noch) keine Angaben gibt.
 *
 * Bauplan: docs/KONZEPT-RECHERCHE-SICHTBARKEIT.md § 2.2 („Quelle + Stand an JEDER Angabe"),
 * § 3.3/§ 6.5 (Impressum als Quelle), § 4.2 (ein Zustand, ein Satz). Kriterium R3.1 in
 * docs/LOOP-RECHERCHE.md.
 *
 * ── Warum eine reine Funktion und kein JSX ────────────────────────────────────────────────────
 * `apps/web` hat KEINEN Test-Runner (in R0.2/R0.7/R0.9 mehrfach festgehalten). Die Regel „eine
 * Angabe ohne Datum wird nicht als datiert ausgegeben" waere in einer React-Komponente
 * unpruefbar. Sie steht deshalb hier; die Tests liegen in `apps/api/test/impressum.test.ts`, weil
 * genau diese Suite in der CI garantiert mitlaeuft. Dieses Paket hat darum bewusst KEIN
 * `test`-Skript (`apps/api/test/ci-testabdeckung.test.ts` verlangt fuer jedes Paket MIT
 * `test`-Skript ein eigenes CI-Ziel — zusaetzliche Mechanik ohne zusaetzliche Sicherheit).
 *
 * ── Was hier NICHT hingehoert ────────────────────────────────────────────────────────────────
 * Die Zustaende („laeuft", „veraltet", „ausgesetzt") bildet `@maestro/anreicherung` ab (R0.7) —
 * dieses Paket RUFT sie auf und baut keinen zweiten Zustandsbaum. Das Format der Herkunftszeile
 * kommt aus `@maestro/recherche-stand` (R1.6). Hier steht nur: welche Felder eine Karte zeigt und
 * wie ein fehlendes Feld benannt wird.
 *
 * ── Reinheit ─────────────────────────────────────────────────────────────────────────────────
 * Kein `new Date()` ohne Argument, keine implizite Zone: `jetzt` und `zeitzone` sind Parameter.
 */
import {
  anreicherungsAnzeige,
  type AnreicherungsHandlung,
  type AnreicherungsZeile,
  type AnreicherungsZustand,
} from "@maestro/anreicherung";
import { standZeile } from "@maestro/recherche-stand";

/* ────────────────────────────── Eingabe ────────────────────────────────────────────────────── */

/**
 * Die Pflichtangaben nach § 5 DDG, so wie die API sie ausliefert. Jedes Feld einzeln nullbar:
 * ein fehlendes Feld ist eine wahre Aussage ueber DIESES Impressum, kein Fehler.
 */
export interface ImpressumAngabenRoh {
  rechtsform?: string | null;
  registernummer?: string | null;
  registergericht?: string | null;
  ustIdNr?: string | null;
  vertreter?: string[] | null;
  anschrift?: string | null;
}

/**
 * Datenanreicherung R3.1: die Zeile aus `impressum_lookups`, wie die API sie als JSON ausliefert
 * (Zeitstempel als ISO-Strings) — Lebenszyklus-Felder wie in `@maestro/anreicherung`.
 */
export interface ImpressumZeile extends AnreicherungsZeile {
  angaben?: ImpressumAngabenRoh | null;
  /** Die URL, aus der die Angaben stammen. */
  quelleUrl?: string | null;
}

export interface ImpressumKarteLage {
  /** Mandanten-Schalter des Moduls (`tenantSettings.impressumEnabled`). */
  modulAktiv: boolean;
  /** Firmenname, zu dem gesucht wurde. */
  firma: string;
  /** Die hinterlegte Website — `null`/leer heisst: es gibt nichts nachzuschlagen. */
  website?: string | null;
  /** Die Lookup-Zeile — `null` heisst: es gibt gar keine Zeile („nie gefragt"). */
  zeile: ImpressumZeile | null;
  jetzt: Date;
  zeitzone?: string;
}

/* ────────────────────────────── Ausgabe ────────────────────────────────────────────────────── */

/** Der Anzeigename des Moduls — exakt wie er in der Modul-Kachel stehen wird. */
export const MODUL_IMPRESSUM = "Impressum-Auslesung";

/** Die Quelle, unter der jede dieser Angaben ausgewiesen wird (`@maestro/recherche-stand`). */
export const QUELLE_IMPRESSUM = "Impressum" as const;

export interface ImpressumAngabe {
  /** Feldbezeichnung, wie sie in der Karte steht. */
  feld: string;
  /** Der Wert. */
  wert: string;
  /** „Impressum · Stand 06.08.2026" — NIE von Hand gebaut (R1.6). */
  herkunft: string;
}

export interface ImpressumKarte {
  zustand: AnreicherungsZustand;
  /** Genau ein Satz — `null`, wenn die Angaben selbst sprechen. */
  satz: string | null;
  handlung: AnreicherungsHandlung | null;
  /** Die gefundenen Angaben, jede mit Quelle + Stand. Leer, wenn es keine gibt. */
  angaben: ImpressumAngabe[];
  /** Welche Pflichtangaben das gelesene Impressum NICHT nennt. Ausdruecklich benannt statt
   *  weggelassen: „nicht enthalten" ist eine Aussage, „fehlt in der Liste" waere keine. */
  nichtEnthalten: string[];
  /** Die URL, aus der die Angaben stammen — der Beleg, den ein Mensch nachschlagen kann. */
  quelleUrl: string | null;
}

/** Reihenfolge und Beschriftung der Felder — eine Liste, damit die Karte nicht sortiert. */
const FELDER: { schluessel: keyof ImpressumAngabenRoh; label: string }[] = [
  { schluessel: "rechtsform", label: "Rechtsform" },
  { schluessel: "registernummer", label: "Registernummer" },
  { schluessel: "registergericht", label: "Registergericht" },
  { schluessel: "ustIdNr", label: "USt-IdNr." },
  { schluessel: "vertreter", label: "Vertretungsberechtigt" },
  { schluessel: "anschrift", label: "Anschrift" },
];

/**
 * Bildet die Datenlage auf eine Karte ab.
 *
 * Reihenfolge der Pruefungen:
 *  1. Modul aus schlaegt alles (wie in `@maestro/anreicherung`).
 *  2. **Keine Website hinterlegt** — ein sauberer Zustand, kein Fehler. Er steht VOR dem Blick in
 *     die Zeile: ob eine Zeile existiert, ist unerheblich, wenn es gar nichts nachzuschlagen
 *     gibt. Abgebildet auf `nie_gefragt` (es hat kein Abruf stattgefunden), aber mit dem Satz,
 *     der die Lage beschreibt, und OHNE Handlung: ein Knopf „Jetzt abrufen" ohne Adresse waere
 *     eine Schaltflaeche ohne Wirkung.
 *  3. Sonst die gemeinsame Zustandsabbildung — ein Zustand, ein Satz, hoechstens eine Handlung.
 */
export function impressumKarte(lage: ImpressumKarteLage): ImpressumKarte {
  const leer = { angaben: [] as ImpressumAngabe[], nichtEnthalten: [] as string[], quelleUrl: null };

  const hatWebsite = Boolean((lage.website ?? "").trim());
  if (lage.modulAktiv && !hatWebsite) {
    return {
      zustand: "nie_gefragt",
      satz: `Zu „${lage.firma}" ist keine Website hinterlegt — es gibt kein Impressum nachzuschlagen.`,
      handlung: null,
      ...leer,
    };
  }

  const anzeige = anreicherungsAnzeige({
    modulAktiv: lage.modulAktiv,
    modulName: MODUL_IMPRESSUM,
    firma: lage.firma,
    zeile: lage.zeile,
    jetzt: lage.jetzt,
    zeitzone: lage.zeitzone,
  });

  const roh = lage.zeile?.angaben ?? null;
  if (!roh || (anzeige.zustand !== "aktuell" && anzeige.zustand !== "veraltet")) {
    return { zustand: anzeige.zustand, satz: anzeige.satz, handlung: anzeige.handlung, ...leer };
  }

  // Der Stand ist der Abrufzeitpunkt der Zeile — nie `created_at`, nie „heute" (R1.6).
  const herkunft = standZeile(QUELLE_IMPRESSUM, lage.zeile?.lookedUpAt, lage.zeitzone);

  const angaben: ImpressumAngabe[] = [];
  const nichtEnthalten: string[] = [];
  for (const { schluessel, label } of FELDER) {
    const wert = roh[schluessel];
    const text = Array.isArray(wert) ? wert.filter(Boolean).join(", ") : (wert ?? "").toString().trim();
    if (text) angaben.push({ feld: label, wert: text, herkunft });
    else nichtEnthalten.push(label);
  }

  return {
    zustand: anzeige.zustand,
    satz: anzeige.satz,
    handlung: anzeige.handlung,
    angaben,
    nichtEnthalten,
    quelleUrl: lage.zeile?.quelleUrl ?? null,
  };
}
